<script setup lang="ts">
import UserSettingsBaseContainer from '~/components/user/user-settings-base-container.vue';
import GenericResponse from '~/components/user-interface/generic-error.vue';
import Tabs from '~/components/design-system/tabs/tabs.vue';
import Tab from '~/components/design-system/tabs/tab.vue';
import { useServices } from '~/composables/useServices';
import type {
  IFilteredCoupons,
  IGenericCoupon,
  IGlobalCoupons,
  IPlayerCoupons,
} from '~/interfaces/dto/coupons/response';
import { useSiteStore } from '~/stores/site.store';
import { GiftIcon } from '@heroicons/vue/24/solid';
import { TicketIcon } from '~/components/design-system/icons';
const { $t } = useNuxtApp();
const { $promotionService } = useServices();
const site = useSiteStore();

const loading = ref<boolean>(true);
const playerCoupons = ref<IPlayerCoupons[] | null>(null);
const globalCoupons = ref<IGlobalCoupons[] | null>(null);
const playerError = ref<boolean>(false);
const globalError = ref<boolean>(false);

const noCoupons = ref<boolean>(false);
const error = ref<boolean>(false);

const getPlayerCoupons = async () => {
  const data = await $promotionService.getAllPlayerCoupons();
  if (data === undefined) playerError.value = true;
  playerCoupons.value = Array.isArray(data) ? data : [];
};

const getGlobalCoupons = async () => {
  const data = await $promotionService.getAllGlobalCoupons(site.regionCode);
  if (data === undefined) globalError.value = true;
  globalCoupons.value = Array.isArray(data) ? data : [];
};

const getAllCoupons = async () => {
  loading.value = true;
  try {
    await Promise.all([getPlayerCoupons(), getGlobalCoupons()]);
  } catch (error) {
    console.error('Error fetching coupons', error);
  } finally {
    if (playerError.value && globalError.value) error.value = true;
    if (playerCoupons.value.length === 0 && globalCoupons.value.length === 0)
      noCoupons.value = true;
    loading.value = false;
  }
};

const standardisePlayerCoupon = (coupon: IPlayerCoupons): IGenericCoupon => ({
  ...coupon,
  status: coupon.status ?? 'allocated',
  isVisible: false,
  expiryDateTime: coupon.expiryDateTime ?? null,
  source: 'player',
});

const standardiseGlobalCoupon = (coupon: IGlobalCoupons): IGenericCoupon => ({
  ...coupon,
  status: coupon.status ?? 'allocated',
  isVisible: false,
  expiryDateTime: null,
  source: 'global',
});

const coupons = computed<IGenericCoupon[]>(() => [
  ...playerCoupons.value.map(standardisePlayerCoupon),
  ...globalCoupons.value.map(standardiseGlobalCoupon),
]);

const filteredCoupons = computed<IFilteredCoupons>(() => {
  return coupons.value.reduce(
    (groups, coupon) => {
      const status = coupon.status?.toLowerCase().replace(/\s+/g, '');
      if (status === 'allocated') groups.allocated.push(coupon);
      if (status === 'activated') groups.activated.push(coupon);
      if (status === 'completed') groups.completed.push(coupon);
      if (status === 'payoutcompleted') groups.payoutcompleted.push(coupon);
      if (status === 'payoutfailed') groups.payoutfailed.push(coupon);
      return groups;
    },
    {
      allocated: [],
      activated: [],
      completed: [],
      payoutcompleted: [],
      payoutfailed: [],
    } as IFilteredCoupons,
  );
});

const isAllocated = computed(() => filteredCoupons.value.allocated.length > 0);
const isActivated = computed(() => filteredCoupons.value.activated.length > 0);
const isCompleted = computed(() => filteredCoupons.value.completed.length > 0);
const isPayOutCompleted = computed(
  () => filteredCoupons.value.payoutcompleted.length > 0,
);
const isPayOutFailed = computed(
  () => filteredCoupons.value.payoutfailed.length > 0,
);
const hasAvailableCoupons = computed(
  () => isAllocated.value || isActivated.value || isPayOutFailed.value,
);
const hasCompletedCoupons = computed(
  () => isCompleted.value || isPayOutCompleted.value,
);
const hasAnyCoupons = computed(
  () => hasAvailableCoupons.value || hasCompletedCoupons.value,
);

onMounted(() => getAllCoupons());
</script>

<template>
  <p
    v-if="!site.webview"
    class="font-bold text-base-priority pb-4 pt-6 hidden md:block"
  >
    {{ $t('gifts-and-offers') }}
  </p>
  <UserSettingsBaseContainer class="!p-0 sm:pt-4">
    <LazyUserInterfaceGenericLoader v-if="loading" class="my-auto" standalone />
    <Tabs v-else-if="hasAnyCoupons" class="bg-base-secondary pt-2">
      <template #tabList>
        <Tab id="available" :title="$t('available')" :icon="TicketIcon">
          <div
            class="bg-layer rounded-xl mx-auto mt-2 md:min-h-96 w-fit min-w-[55%]"
          >
            <div v-if="isActivated">
              <p class="font-bold pt-4 pl-4">
                {{ $t('coupons-inprogress') }}
              </p>
              <LazyAccountGiftsOffersSingleList
                :filtered-coupons="filteredCoupons.activated"
                indexing="activated"
                @activated="getAllCoupons"
              />
            </div>
            <div v-if="isAllocated">
              <p class="font-bold pt-4 pl-4">
                {{ $t('coupons-inactive') }}
              </p>
              <LazyAccountGiftsOffersSingleList
                :filtered-coupons="filteredCoupons.allocated"
                indexing="allocated"
                @activated="getAllCoupons"
              />
            </div>
            <div v-if="isPayOutFailed">
              <LazyAccountGiftsOffersSingleList
                :filtered-coupons="filteredCoupons.payoutfailed"
                indexing="error"
                @activated="getAllCoupons"
              />
            </div>
            <div
              v-if="!isActivated && !isAllocated && !isPayOutFailed"
              class="relative p-4"
            >
              <GenericResponse class="place-items-center m-4" state="info">
                {{ $t('coupons-unavailable') }}
              </GenericResponse>
            </div>
          </div>
        </Tab>
        <Tab id="complete" :title="$t('gifts')" :icon="GiftIcon">
          <div
            class="bg-layer rounded-xl mx-auto mt-2 md:min-h-96 w-fit min-w-[55%]"
          >
            <div v-if="isCompleted || isPayOutCompleted">
              <p class="font-bold pt-4 pl-4">
                {{ $t('coupons-completed') }}
              </p>
              <LazyAccountGiftsOffersSingleList
                v-if="isCompleted"
                :filtered-coupons="filteredCoupons.completed"
                indexing="completed"
                @activated="getAllCoupons"
              />
              <LazyAccountGiftsOffersSingleList
                v-if="isPayOutCompleted"
                :filtered-coupons="filteredCoupons.payoutcompleted"
                indexing="payoutCompleted"
                @activated="getAllCoupons"
              />
            </div>
            <div v-if="!isCompleted && !isPayOutCompleted" class="relative p-4">
              <GenericResponse class="place-items-center m-4" state="info">
                {{ $t('coupons-unavailable') }}
              </GenericResponse>
            </div>
          </div>
        </Tab>
      </template>
    </Tabs>
    <GenericResponse v-else-if="error" class="relative p-4" state="danger">
      {{ $t('coupons-error') }}
    </GenericResponse>
    <GenericResponse v-else-if="noCoupons" class="relative p-4" state="info">
      {{ $t('coupons-unavailable') }}
    </GenericResponse>
  </UserSettingsBaseContainer>
</template>

<style scoped lang="scss"></style>
